import React from 'react';

import {
  HighlightableString,
  makeHighlightableString,
} from '@/app/log-viewer/highlighting/HighlightableString';
import { HighlightableStringView } from '@/app/log-viewer/highlighting/HighlightableStringView';
import { LogEntry } from '@/parsing/LogEntry';
import { ReactNativeLogEntryData } from '@/parsing/parsers/reactnative/types';

import { LogEntryCellModel, LogEntryCellModelData, LogEntryCellType } from '../LogEntryCellModel';

export interface LogEntryReduxActionModelData extends LogEntryCellModelData {
  actionType: HighlightableString;
  actionPayload: HighlightableString;
  isTruncated: boolean;
}

export interface LogEntryReduxActionCellModel extends LogEntryCellModel {
  data: LogEntryReduxActionModelData;
}

export abstract class LogEntryReduxActionCellModelFactory {
  static make(logEntry: LogEntry): LogEntryReduxActionCellModel {
    let entryData: ReactNativeLogEntryData<'reduxAction'>;
    try {
      entryData = JSON.parse(logEntry.content).data;
    } catch (e) {
      // Content can be corrupted, e.g. when a following non-timestamped line is
      // appended to this entry and breaks the JSON. Show the raw content instead
      // of failing the whole table.
      return {
        applySettings: () => {},
        data: {
          type: LogEntryCellType.ReduxAction,
          actionType: makeHighlightableString('(unparseable redux action)'),
          actionPayload: makeHighlightableString(logEntry.content ?? ''),
          isTruncated: logEntry.originalLines[0]?.content.endsWith('<truncated>') ?? false,
        },
      };
    }

    return {
      applySettings: () => {},
      data: {
        type: LogEntryCellType.ReduxAction,
        actionType: makeHighlightableString(entryData.type),
        actionPayload: makeHighlightableString(JSON.stringify(entryData.payload ?? {}, null, 4)),
        isTruncated: logEntry.originalLines[0]?.content.endsWith('<truncated>') ?? false,
      },
    };
  }
}

interface LogEntryReduxActionCellProps {
  model: LogEntryReduxActionCellModel;
}

export const LogEntryReduxActionCell = ({ model }: LogEntryReduxActionCellProps) => {
  return (
    <>
      <HighlightableStringView className="redux-action-cell-title" string={model.data.actionType} />
      {model.data.isTruncated ? <p className="yellow-700">🥹 This is a truncated log</p> : null}
      <HighlightableStringView
        className="verbatim-multiline whitespace-pre"
        string={model.data.actionPayload}
      />
    </>
  );
};
